/**
 * Optional Web Audio processing chain layered on top of the plain <audio> element
 * that `player.ts` owns. Three constraints shape everything here:
 *
 * 1. `createMediaElementSource` can be called EXACTLY ONCE for the lifetime of a
 *    given HTMLMediaElement, across every AudioContext that will ever exist, in
 *    every browser. A second call throws (Chrome/Firefox) or silently produces a
 *    dead node (older WebKit). Once it succeeds, the element's audio output is
 *    permanently rerouted through the Web Audio graph: nothing plays again unless
 *    something in that graph reaches `context.destination`. We guard the call with
 *    a module level WeakSet so an accidental double call (React strict mode, hot
 *    reload, a second createAudioChain on the same element) degrades to a no-op
 *    chain instead of throwing or going silent.
 * 2. AudioContext is created (or resumed from) the "suspended" state until a user
 *    gesture unlocks it. Without an explicit resume() on the first play, iOS Safari
 *    plays nothing at all while looking like it is playing. We resume on every
 *    "play" event, not just the first, because the OS can re-suspend a context
 *    (screen lock, phone call) and playback must recover on its own.
 * 3. When every option is off, the chain must be inert: source connects straight
 *    to a unity gain node and then to destination, no filters, no compressor. That
 *    way silence, boost and normalize are all strictly opt-in and the shipped
 *    default sounds exactly like a bare <audio> element.
 */

const capturedElements = new WeakSet<HTMLAudioElement>();

export interface AudioChainOptions {
  boost: boolean;
  normalize: boolean;
  trimSilence: boolean;
}

export interface AudioChain {
  update(options: AudioChainOptions): void;
  /** 1 = full volume, 0 = silent. Used by the sleep timer fade out. */
  setFade(factor: number): void;
  /** Call on every animation frame while playing; drives silence skipping. */
  tick(): void;
  destroy(): void;
}

function sameOptions(a: AudioChainOptions, b: AudioChainOptions): boolean {
  return a.boost === b.boost && a.normalize === b.normalize && a.trimSilence === b.trimSilence;
}

function dbToLinear(db: number): number {
  return Math.pow(10, db / 20);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** A chain that does nothing: Web Audio unsupported, or the element was already captured. */
function inertChain(audio: HTMLAudioElement): AudioChain {
  return {
    update: () => {},
    // No gain node to drive, so fall back to the element's own volume for the fade.
    setFade: (factor) => {
      audio.volume = clamp01(factor);
    },
    tick: () => {},
    destroy: () => {},
  };
}

// Voice presence sits around 2 to 4 kHz; a small bump there makes narration read as
// clearer without sounding thin or sibilant. Anything more aggressive fights the
// codec's own high frequency rolloff and starts to sound harsh.
const PRESENCE_FREQUENCY = 3000;
const PRESENCE_GAIN_DB = 3;
const PRESENCE_Q = 1;

// Below 80 Hz there is no voice content on a spoken word recording, only room rumble
// and handling noise. Cutting it frees headroom for the compressor and the presence
// bump without touching anything the listener actually hears.
const HIGHPASS_FREQUENCY = 80;
const HIGHPASS_Q = 0.7;

// Gentle "doux" compression settings for narration: a threshold low enough to catch
// quiet passages, a soft knee so the transition is not audible as pumping, a mild
// 3:1 ratio (not the 12:1+ used for broadcast loudness), a fast-ish attack so plosives
// do not poke through, and a release long enough to avoid audible breathing between
// words. These are close to the platform defaults on purpose: the goal is "louder and
// steadier", not "obviously processed".
const COMPRESSOR_THRESHOLD_DB = -28;
const COMPRESSOR_KNEE_DB = 24;
const COMPRESSOR_RATIO = 3;
const COMPRESSOR_ATTACK_S = 0.01;
const COMPRESSOR_RELEASE_S = 0.25;
// How much of the compressor's own gain reduction we hand back as makeup gain. 1.0
// would restore full loudness but can clip on transients right after a quiet patch;
// 0.8 stays a hair under that so peaks keep a little headroom.
const MAKEUP_COMPENSATION = 0.8;
const MAKEUP_SMOOTHING_S = 0.05;

// Silence trimming is the option most likely to wreck a listen if it is too eager,
// so every constant here leans conservative on purpose:
// - the RMS threshold is low (-45 dBFS): only genuine dead air trips it, not a soft
//   or breathy line reading;
// - the signal must stay under threshold for a full 900 ms before we act, which is
//   longer than virtually any natural pause taken mid sentence, so a comma or a
//   breath never gets mistaken for silence;
// - the exit threshold is higher than the entry threshold (hysteresis): a brief
//   loud blip while we are already "in silence" does not reset the clock, but going
//   quiet does not falsely retrigger right at the boundary either;
// - the RMS itself is smoothed with a one-pole average so a single quiet analysis
//   frame (about 10 ms) cannot start the clock on its own;
// - and no matter what the signal does, at most one skip happens per second, so a
//   patch of intermittent near-silence cannot turn into a stutter of tiny jumps.
// The cost of all this caution is that some short silences are simply never
// skipped. That is the right trade: missing a skip is inaudible, cutting into a
// word is not.
const SILENCE_ENTER_RMS = dbToLinear(-45);
const SILENCE_EXIT_RMS = dbToLinear(-38);
const SILENCE_HOLD_MS = 900;
const SILENCE_SKIP_SECONDS = 2;
const SILENCE_MIN_INTERVAL_MS = 1000;
const ANALYSER_FFT_SIZE = 512;

export function createAudioChain(audio: HTMLAudioElement, onSkip: (seconds: number) => void): AudioChain {
  if (capturedElements.has(audio)) {
    // Someone already ran createMediaElementSource on this element. Calling it again
    // would throw and would not undo the first capture, so stay silent about it and
    // hand back a chain that leaves the element's native playback alone.
    return inertChain(audio);
  }

  const AudioContextCtor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return inertChain(audio);

  const context = new AudioContextCtor();

  let source: MediaElementAudioSourceNode;
  try {
    source = context.createMediaElementSource(audio);
  } catch {
    // Some other code path already captured this element in a context we do not
    // control. Close ours and fall back rather than leaving a dangling context.
    void context.close().catch(() => {});
    return inertChain(audio);
  }
  capturedElements.add(audio);

  const highpass = context.createBiquadFilter();
  highpass.type = "highpass";
  highpass.frequency.value = HIGHPASS_FREQUENCY;
  highpass.Q.value = HIGHPASS_Q;

  const presence = context.createBiquadFilter();
  presence.type = "peaking";
  presence.frequency.value = PRESENCE_FREQUENCY;
  presence.Q.value = PRESENCE_Q;
  presence.gain.value = PRESENCE_GAIN_DB;

  const compressor = context.createDynamicsCompressor();
  compressor.threshold.value = COMPRESSOR_THRESHOLD_DB;
  compressor.knee.value = COMPRESSOR_KNEE_DB;
  compressor.ratio.value = COMPRESSOR_RATIO;
  compressor.attack.value = COMPRESSOR_ATTACK_S;
  compressor.release.value = COMPRESSOR_RELEASE_S;

  const makeupGain = context.createGain();
  makeupGain.gain.value = 1;

  // Always present, always the last node before destination. Starts at unity so
  // playback is untouched until the sleep timer actually starts fading.
  const fadeGain = context.createGain();
  fadeGain.gain.value = 1;
  fadeGain.connect(context.destination);

  // Tapped straight off the source, upstream of every optional effect, so silence
  // detection sees the real signal regardless of which options are on. It never
  // needs an output connection: an AnalyserNode analyses whatever reaches its
  // input whether or not anything is downstream of it.
  const analyser = context.createAnalyser();
  analyser.fftSize = ANALYSER_FFT_SIZE;
  const silenceBuffer = new Float32Array(analyser.fftSize);

  let currentOptions: AudioChainOptions = { boost: false, normalize: false, trimSilence: false };

  function rebuildGraph(options: AudioChainOptions): void {
    // Tear every optional node's connections down before rebuilding: disconnect()
    // on `source` removes the analyser tap too, so it is always re-created below.
    source.disconnect();
    highpass.disconnect();
    presence.disconnect();
    compressor.disconnect();
    makeupGain.disconnect();

    source.connect(analyser);

    let node: AudioNode = source;
    if (options.boost) {
      node.connect(highpass);
      node = highpass;
      node.connect(presence);
      node = presence;
    }
    if (options.normalize) {
      node.connect(compressor);
      node = compressor;
      node.connect(makeupGain);
      node = makeupGain;
    } else {
      // No compressor in the graph: nothing is driving makeup gain, so keep it at
      // unity in case normalize gets re-enabled and it briefly reconnects mid ramp.
      makeupGain.gain.value = 1;
    }
    node.connect(fadeGain);
  }

  // Inert by construction: nothing is connected to `fadeGain` besides the direct
  // source path built by rebuildGraph, and that path is a straight wire until an
  // option turns something on.
  rebuildGraph(currentOptions);

  const resume = (): void => {
    if (context.state === "suspended") void context.resume().catch(() => {});
  };
  audio.addEventListener("play", resume);

  let silenceSince: number | null = null;
  let smoothedRms = 1;
  let lastSkipAt = 0;

  return {
    update(options) {
      if (sameOptions(options, currentOptions)) return;
      currentOptions = options;
      rebuildGraph(options);
      if (!options.trimSilence) silenceSince = null;
    },

    setFade(factor) {
      const value = clamp01(factor);
      // A short ramp instead of an instant jump avoids an audible click when the
      // sleep timer nudges the volume down step by step.
      fadeGain.gain.setTargetAtTime(value, context.currentTime, 0.05);
    },

    tick() {
      if (currentOptions.normalize) {
        // compressor.reduction is how many dB of gain the compressor is currently
        // pulling out (always <= 0). Feeding it back as makeup gain keeps loudness
        // roughly constant whether or not the compressor is actively working,
        // instead of guessing a single fixed makeup value that is wrong most of
        // the time.
        const target = dbToLinear(-compressor.reduction * MAKEUP_COMPENSATION);
        makeupGain.gain.setTargetAtTime(target, context.currentTime, MAKEUP_SMOOTHING_S);
      }

      if (!currentOptions.trimSilence) {
        silenceSince = null;
        return;
      }

      analyser.getFloatTimeDomainData(silenceBuffer);
      let sumSquares = 0;
      for (let i = 0; i < silenceBuffer.length; i++) {
        const sample = silenceBuffer[i];
        sumSquares += sample * sample;
      }
      const rms = Math.sqrt(sumSquares / silenceBuffer.length);
      smoothedRms = smoothedRms * 0.8 + rms * 0.2;

      const now = performance.now();
      if (silenceSince === null) {
        if (smoothedRms < SILENCE_ENTER_RMS) silenceSince = now;
      } else if (smoothedRms > SILENCE_EXIT_RMS) {
        silenceSince = null;
      } else if (now - silenceSince > SILENCE_HOLD_MS && now - lastSkipAt > SILENCE_MIN_INTERVAL_MS) {
        lastSkipAt = now;
        silenceSince = null;
        onSkip(SILENCE_SKIP_SECONDS);
      }
    },

    destroy() {
      audio.removeEventListener("play", resume);
      source.disconnect();
      highpass.disconnect();
      presence.disconnect();
      compressor.disconnect();
      makeupGain.disconnect();
      fadeGain.disconnect();
      analyser.disconnect();
      // The element stays permanently bound to this (now closed) context per spec;
      // it simply never plays through Web Audio again, which is fine since the
      // page is tearing this chain down anyway.
      void context.close().catch(() => {});
    },
  };
}
